import { NudgeEventTypes, NudgeTypes } from './constants';
import { isDevMode } from 'whisper-base';
/**
 * Prefix used for storing nudge tracking data in sessionStorage.
 * Each session will have its own storage key using this prefix.
 */
const STORAGE_PREFIX = 'whisper_active_nudge_fingerprints';

/**
 * Set of frontend nudge types that require collision tracking.
 * These types of nudges should not be displayed simultaneously
 * to prevent UI conflicts. Examples include:
 * - Modals and popups that would overlap
 * - Tooltips that would compete for attention
 * - Interactive elements that require exclusive focus
 */
const feCollisionTrackedNudgeTypes = new Set([NudgeTypes.MODAL, NudgeTypes.POPUP_GROWTH_PROMPT, NudgeTypes.ONBOARDING_DEMO, NudgeTypes.ONBOARDING_TOUR, NudgeTypes.COACHING_TIP_PULSER, NudgeTypes.COACHING_TIP_TOOLTIP, NudgeTypes.POPUP_CSAT, NudgeTypes.POPUP_NOTIFICATIONS]);

/**
 * Manages the state of active nudges across frames using both local maps and sessionStorage.
 * This class:
 * - Tracks which nudges are currently displayed
 * - Prevents multiple nudges from displaying simultaneously when they would conflict
 * - Synchronizes nudge state across different frames using sessionStorage
 * - Provides cleanup mechanisms to prevent memory leaks
 */
export class NudgeStateManager {
  constructor(sessionId, logger) {
    this.activeNudges = new WeakMap();
    this.nudgeFingerprints = new Map();
    if (!sessionId) {
      throw new Error('sessionId is required for NudgeStateManager');
    }
    this.sessionId = sessionId;
    this.storageKey = `${STORAGE_PREFIX}_${this.sessionId}`;
    this.logger = logger;
    window.addEventListener('pagehide', () => {
      this.cleanup();
    });
  }

  /**
   * Maps active nudge DOM elements to their unique fingerprints.
   * Uses WeakMap to allow garbage collection of removed elements,
   * preventing memory leaks when nudges are removed from the DOM.
   * This is the primary tracking mechanism for active nudges within a frame.
   */

  /**
   * Maps fingerprints back to their nudge DOM elements.
   * Used in conjunction with activeNudges for bidirectional lookup.
   * Fingerprints are crucial for cross-frame tracking since DOM elements
   * cannot be stored in sessionStorage. The fingerprints serve as
   * serializable identifiers that can be shared between frames.
   */

  /**
   * Updates the stored set of active nudge fingerprints in sessionStorage.
   * This ensures that other frames can check which nudges are currently active
   * and prevent conflicting nudges from being displayed.
   * We only store the fingerprints since DOM elements cannot be serialized,
   * and each frame maintains its own element-to-fingerprint mapping.
   */
  updateStoredNudgeFingerprints() {
    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(Array.from(this.nudgeFingerprints.keys())));
    } catch (error) {
      this.logger.error('Error storing nudge fingerprints', error);
    }
  }

  /**
   * Adds a new nudge element to tracking.
   * This method:
   * 1. Ensures one-to-one mapping between elements and fingerprints
   * 2. Generates a unique fingerprint for the nudge (required for cross-frame tracking)
   * 3. Updates both local tracking maps
   * 4. Synchronizes fingerprints to sessionStorage (since elements can't be stored there)
   *
   * @param {HTMLElement} nudgeElement - The DOM element representing the nudge to track
   * @returns {string} The generated fingerprint for the nudge, used for cross-frame identification
   */
  trackNudge(nudgeElement) {
    // Ensure one-to-one mapping between element and nudge
    const existingFingerprint = this.activeNudges.get(nudgeElement);
    if (existingFingerprint) {
      this.activeNudges.delete(nudgeElement);
      this.nudgeFingerprints.delete(existingFingerprint);
      this.updateStoredNudgeFingerprints();
    }

    // Add new tracking
    const nudgeFingerprint = crypto.randomUUID();
    this.activeNudges.set(nudgeElement, nudgeFingerprint);
    this.nudgeFingerprints.set(nudgeFingerprint, nudgeElement);
    this.updateStoredNudgeFingerprints();
    return nudgeFingerprint;
  }

  /**
   * Removes a nudge element from tracking and cleans up any inconsistencies.
   * This method:
   * 1. Removes the element and its fingerprint from both tracking maps
   * 2. Updates sessionStorage to maintain cross-frame state
   * 3. Returns the removed fingerprint for potential further processing
   *
   * @param {HTMLElement} nudgeElement - The DOM element to remove from tracking
   * @returns {string | null} The fingerprint that was removed, or null if not found
   */
  untrackNudge(nudgeElement) {
    const nudgeFingerprint = this.activeNudges.get(nudgeElement);
    if (!nudgeFingerprint) {
      return null;
    }

    // Remove from local tracking
    this.activeNudges.delete(nudgeElement);
    this.nudgeFingerprints.delete(nudgeFingerprint);
    this.updateStoredNudgeFingerprints();
    return nudgeFingerprint;
  }

  /**
   * Checks if any nudges are currently being displayed across all frames.
   * This method:
   * 1. Scans all sessionStorage keys with our prefix
   * 2. Checks each key for active nudge fingerprints
   * 3. Returns false if any active nudges are found
   * 4. Logs the blocking nudge in dev mode for debugging
   *
   * @returns {boolean} True if no nudges are active, false if any nudge is displayed
   */
  canDisplayNudges() {
    try {
      // Check all storage keys for any active nudges
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key !== null && key !== void 0 && key.startsWith(STORAGE_PREFIX)) {
          const stored = sessionStorage.getItem(key);
          if (stored) {
            const fingerprints = JSON.parse(stored);
            if (fingerprints.length > 0) {
              if (isDevMode()) {
                this.logger.info('Can not display nudges because of the following active nudge: ', this.nudgeFingerprints.get(fingerprints[0]));
              }
              return false;
            }
          }
        }
      }
      return true;
    } catch (error) {
      this.logger.error('Error checking if nudges can be displayed', error);
      return true;
    }
  }

  /**
   * Handles collision tracking for nudges that require exclusive display.
   * This method:
   * 1. Checks if the nudge type requires collision tracking
   * 2. Tracks new nudges when they are rendered
   * 3. Removes tracking when nudges are dismissed
   * 4. Maintains cross-frame synchronization through the process
   *
   * @param element - The DOM element representing the nudge
   * @param nudgeType - The type of the nudge (modal, tooltip, etc.)
   * @param eventType - The type of event (rendered or dismissed)
   */
  handleCollisionTracking(element, nudgeType, eventType) {
    try {
      if (feCollisionTrackedNudgeTypes.has(nudgeType)) {
        if (eventType === NudgeEventTypes.RENDERED) {
          this.trackNudge(element);
        } else if (eventType === NudgeEventTypes.DISMISSED) {
          this.untrackNudge(element);
        }
      }
    } catch (error) {
      this.logger.error('Error handling collision tracking', error);
    }
  }

  /**
   * Performs complete cleanup of the nudge tracking system.
   * This method:
   * 1. Removes all entries from both tracking maps
   * 2. Clears the sessionStorage entry for this session
   * 3. Ensures no memory leaks from lingering references
   *
   * Called automatically on page hide and when manually shutting down tracking.
   */
  cleanup() {
    try {
      for (const element of this.nudgeFingerprints.values()) {
        this.activeNudges.delete(element);
      }
      this.nudgeFingerprints.clear();
      sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      this.logger.error('Error cleaning up nudge state', error);
    }
  }
}